import React from 'react';
import { PenTool, Building, Wrench, Route, RefreshCw, Sofa, ClipboardList, Settings, Leaf } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';

const Divisions = () => {
  const { t, language } = useLanguage();

  const divisions = [
    {
      icon: PenTool,
      title: language === 'en' ? 'Design & Engineering' : 'التصميم والهندسة',
      description: language === 'en' 
        ? 'Architecture, structural and MEP engineering, 3D visualization, value engineering and authority coordination.'
        : 'الهندسة المعمارية والإنشائية والكهروميكانيكية، التصور ثلاثي الأبعاد، هندسة القيمة والتنسيق مع الجهات المختصة.',
      image: '/assets/images/divisions/design-engineering.jpg'
    },
    {
      icon: Building,
      title: language === 'en' ? 'Civil Construction' : 'الإنشاءات المدنية',
      description: language === 'en' 
        ? 'Structural, architectural and building works across residential, commercial, industrial and public-sector assets.'
        : 'الأعمال الإنشائية والمعمارية والبناء عبر الأصول السكنية والتجارية والصناعية والقطاع العام.',
      image: '/assets/images/construction-division.jpg'
    },
    {
      icon: Wrench,
      title: language === 'en' ? 'Electromechanical' : 'الأعمال الكهروميكانيكية',
      description: language === 'en' 
        ? 'Electrical, plumbing, HVAC, fire-fighting, low-current, smart-building and special systems.'
        : 'الأنظمة الكهربائية والسباكة والتكييف ومكافحة الحرائق والتيار المنخفض والمباني الذكية والأنظمة الخاصة.',
      image: '/assets/images/mep-division.jpg'
    },
    {
      icon: Route,
      title: language === 'en' ? 'Infrastructure & External' : 'البنية التحتية والأعمال الخارجية',
      description: language === 'en' 
        ? 'Water networks, pump stations, roads, paving, hardscape, landscape, external lighting and utilities.'
        : 'شبكات المياه ومحطات الضخ والطرق والرصف والأرضيات الصلبة والمناظر الطبيعية والإنارة الخارجية والمرافق.',
      image: '/assets/images/divisions/infrastructure-external.jpg'
    },
    {
      icon: RefreshCw,
      title: language === 'en' ? 'Renovation & Retrofit' : 'التجديد وإعادة التأهيل',
      description: language === 'en' 
        ? 'Asset enhancement, waterproofing, building upgrades, MEP retrofit and phased renovation works.'
        : 'تحسين الأصول والعزل المائي وترقية المباني وتحديث الأنظمة الكهروميكانيكية وأعمال التجديد المرحلية.',
      image: '/assets/images/divisions/renovation-retrofit.jpg'
    },
    {
      icon: Sofa,
      title: language === 'en' ? 'Interior Fit-Out & FF&E' : 'التشطيبات الداخلية والأثاث والتجهيزات والمعدات',
      description: language === 'en' 
        ? 'Turnkey interiors, joinery, finishes, furniture, fixtures, equipment and handover-ready spaces.'
        : 'التصميم الداخلي المتكامل والنجارة والتشطيبات والأثاث والتركيبات والمعدات والمساحات الجاهزة للتسليم.',
      image: '/assets/images/divisions/interior-fitout-ffe.jpg'
    },
    {
      icon: ClipboardList,
      title: language === 'en' ? 'Procurement & Project Management' : 'المشتريات وإدارة المشاريع',
      description: language === 'en' 
        ? 'Sourcing, cost control, planning, quality, reporting, risk and stakeholder coordination.'
        : 'التوريد والتحكم في التكاليف والتخطيط والجودة وإعداد التقارير وإدارة المخاطر وتنسيق أصحاب المصلحة.',
      image: '/assets/images/divisions/procurement-project-management.jpg'
    },
    {
      icon: Settings,
      title: language === 'en' ? 'Operation & Maintenance' : 'التشغيل والصيانة',
      description: language === 'en' 
        ? 'Preventive and corrective maintenance, asset management and facility-performance continuity.'
        : 'الصيانة الوقائية والتصحيحية وإدارة الأصول واستمرارية أداء المرافق.',
      image: '/assets/images/operations-division.jpg'
    },
    {
      icon: Leaf,
      title: language === 'en' ? 'Sustainability & Development' : 'الاستدامة والتطوير',
      description: language === 'en' 
        ? 'Renewable energy, efficiency, storage, clean mobility, waste valorization and decarbonization.'
        : 'الطاقة المتجددة والكفاءة والتخزين والتنقل النظيف وتثمين النفايات وخفض الكربون.',
      image: '/assets/images/sustainability-division.jpg'
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero */}
      <section className="bg-[#141F48] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('divisionsTitle')}</h1>
          <p className="text-xl text-[#D3BC8D] font-medium">{t('divisionsSubtitle')}</p>
        </div>
      </section>

      {/* Divisions Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {divisions.map((division, index) => {
              const Icon = division.icon;
              // Use lighter overlay for Interior Fit-Out card (index 5)
              const overlayOpacity = index === 5 ? 'bg-[#141F48]/40' : 'bg-[#141F48]/60';
              const overlayHoverOpacity = index === 5 ? 'bg-[#141F48]/20' : 'bg-[#141F48]/40';
              
              // Icon positioning: LTR (left), RTL (right)
              const iconPositionStyle = language === 'ar' 
                ? { right: '16px', left: 'auto' } 
                : { left: '16px', right: 'auto' };
              
              return (
                <Card key={index} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={division.image}
                      alt={division.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 ${overlayOpacity} group-hover:${overlayHoverOpacity} transition-colors duration-300`} />
                    <div className="absolute top-4" style={iconPositionStyle}>
                      <div className="w-12 h-12 bg-[#D3BC8D] rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-[#141F48]" />
                      </div>
                    </div>
                  </div>
                  <CardContent className={`p-6 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <h3 className="text-xl font-bold text-[#141F48] mb-3 min-h-[3.5rem] flex items-center">
                      {division.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{division.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Divisions;
